import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  financialYearsApi,
  type ConfirmCloseRequest,
  type LockRequest,
  type CreateFinancialYearRequest,
  type UpdateFinancialYearRequest,
} from '../services/financialYearsApi';

const ROOT_KEY = ['financial-years'] as const;

export const useFinancialYears = () =>
  useQuery({
    queryKey: ROOT_KEY,
    queryFn: financialYearsApi.list,
  });

/**
 * The FY containing today. Used by the global footer badge — kept silent on errors so
 * the badge just disappears when the user lacks `financial_years.view` or no FY exists.
 */
export const useCurrentFinancialYear = (enabled = true) =>
  useQuery({
    queryKey: [...ROOT_KEY, 'current'],
    queryFn: financialYearsApi.current,
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

export const useFinancialYear = (id: string | undefined) =>
  useQuery({
    queryKey: [...ROOT_KEY, id],
    queryFn: () => financialYearsApi.getById(id as string),
    enabled: !!id,
  });

export const useFinancialYearPreflight = (id: string | undefined) =>
  useQuery({
    queryKey: [...ROOT_KEY, id, 'preflight'],
    queryFn: () => financialYearsApi.preflight(id as string),
    enabled: !!id,
    // Preflight is a live read — refresh while the FM is reviewing the page.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

export const useFinancialYearSnapshot = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: [...ROOT_KEY, id, 'snapshot'],
    queryFn: () => financialYearsApi.snapshot(id as string),
    enabled: !!id && enabled,
    retry: false, // 404 is expected for Open years; don't burn retries on it
  });

const invalidateAll = () => {
  queryClient.invalidateQueries({ queryKey: ROOT_KEY });
};

export const useCreateFinancialYear = () =>
  useMutation({
    mutationFn: (payload: CreateFinancialYearRequest) => financialYearsApi.create(payload),
    onSuccess: invalidateAll,
  });

export const useUpdateFinancialYear = () =>
  useMutation({
    mutationFn: ({ id, payload, gmOverride }: { id: string; payload: UpdateFinancialYearRequest; gmOverride?: boolean }) =>
      financialYearsApi.update(id, payload, gmOverride),
    onSuccess: invalidateAll,
  });

export const useFinancialYearHasTransactions = (id: string | undefined) =>
  useQuery({
    queryKey: [...ROOT_KEY, id, 'has-transactions'],
    queryFn: () => financialYearsApi.hasTransactions(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });

export const useStartCloseFinancialYear = () =>
  useMutation({
    mutationFn: (id: string) => financialYearsApi.startClose(id),
    onSuccess: invalidateAll,
  });

export const useConfirmCloseFinancialYear = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ConfirmCloseRequest }) =>
      financialYearsApi.confirmClose(id, payload),
    onSuccess: invalidateAll,
  });

export const useCancelCloseFinancialYear = () =>
  useMutation({
    mutationFn: (id: string) => financialYearsApi.cancelClose(id),
    onSuccess: invalidateAll,
  });

export const useLockFinancialYear = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LockRequest }) =>
      financialYearsApi.lock(id, payload),
    onSuccess: invalidateAll,
  });

export const useApproveFinancialYearCreation = () =>
  useMutation({
    mutationFn: (id: string) => financialYearsApi.approveCreation(id),
    onSuccess: invalidateAll,
  });

export const useRejectFinancialYearCreation = () =>
  useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      financialYearsApi.rejectCreation(id, reason),
    onSuccess: invalidateAll,
  });
